import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Eye, EyeOff, Check, X, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Why Passwords Matter",
        icon: "🔐",
        content: (
            <div className="space-y-6">
                <div className="bg-red-50 p-6 rounded-2xl border-2 border-red-200">
                    <h3 className="font-bold text-xl text-red-800 mb-3">Your First Line of Defense</h3>
                    <p className="text-slate-700 mb-4">
                        A <strong>password</strong> is like the key to your digital world. It protects your
                        accounts, personal information, and privacy from hackers and bad actors.
                    </p>
                    <p className="text-slate-700">
                        Weak passwords can be cracked in <strong>seconds</strong>. Strong passwords can take
                        <strong> centuries</strong> to break!
                    </p>
                </div>
                <div className="bg-amber-50 p-5 rounded-xl border border-amber-200">
                    <h4 className="font-bold text-amber-800 mb-3">🚨 Scary Stats:</h4>
                    <ul className="text-amber-700 space-y-2">
                        <li>• "123456" is still the most common password</li>
                        <li>• 81% of data breaches are due to weak passwords</li>
                        <li>• Hackers can try 10 billion guesses per second!</li>
                    </ul>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "How Hackers Crack Passwords",
        icon: "🕵️",
        content: (
            <div className="space-y-4">
                <p className="text-slate-600 text-lg mb-4">Hackers use several methods to crack passwords:</p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-red-50 p-5 rounded-xl border-2 border-red-200">
                        <h3 className="font-bold text-red-700 mb-2">Brute Force Attack</h3>
                        <p className="text-slate-600 text-sm mb-2">
                            Tries every possible combination. "aaaa" → "aaab" → "aaac".
                            Modern computers try billions per second!
                        </p>
                        <div className="text-xs text-red-500 font-bold">
                            Defense: Long passwords (12+ characters)
                        </div>
                    </div>
                    <div className="bg-orange-50 p-5 rounded-xl border-2 border-orange-200">
                        <h3 className="font-bold text-orange-700 mb-2">Dictionary Attack</h3>
                        <p className="text-slate-600 text-sm mb-2">
                            Uses lists of common words and passwords.
                            "password123" takes milliseconds to crack.
                        </p>
                        <div className="text-xs text-orange-500 font-bold">
                            Defense: Avoid real words, use random strings
                        </div>
                    </div>
                    <div className="bg-yellow-50 p-5 rounded-xl border-2 border-yellow-200">
                        <h3 className="font-bold text-yellow-700 mb-2">Rainbow Tables</h3>
                        <p className="text-slate-600 text-sm mb-2">
                            Pre-computed tables of password hashes.
                            If your password's hash is in the table, it's cracked instantly.
                        </p>
                        <div className="text-xs text-yellow-600 font-bold">
                            Defense: Websites should use "salted" hashes
                        </div>
                    </div>
                    <div className="bg-purple-50 p-5 rounded-xl border-2 border-purple-200">
                        <h3 className="font-bold text-purple-700 mb-2">Social Engineering</h3>
                        <p className="text-slate-600 text-sm mb-2">
                            Guessing based on your personal info: pet names,
                            birthdays, favorite teams, etc.
                        </p>
                        <div className="text-xs text-purple-500 font-bold">
                            Defense: Never use personal info in passwords
                        </div>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "What Makes a Strong Password",
        icon: "💪",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 text-lg">A strong password has these characteristics:</p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-green-50 p-4 rounded-xl border-2 border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                            <Check className="text-green-600" size={20} />
                            <span className="font-bold text-green-700">12+ Characters</span>
                        </div>
                        <p className="text-slate-600 text-sm">Each extra character makes it exponentially harder to crack</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border-2 border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                            <Check className="text-green-600" size={20} />
                            <span className="font-bold text-green-700">Mix of Characters</span>
                        </div>
                        <p className="text-slate-600 text-sm">Uppercase, lowercase, numbers, and symbols</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border-2 border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                            <Check className="text-green-600" size={20} />
                            <span className="font-bold text-green-700">No Real Words</span>
                        </div>
                        <p className="text-slate-600 text-sm">Dictionary attacks can guess words instantly</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border-2 border-green-200">
                        <div className="flex items-center gap-2 mb-2">
                            <Check className="text-green-600" size={20} />
                            <span className="font-bold text-green-700">No Personal Info</span>
                        </div>
                        <p className="text-slate-600 text-sm">Birthday, pet name, address are easy to guess</p>
                    </div>
                </div>
                <div className="bg-blue-50 p-5 rounded-xl border border-blue-200">
                    <h4 className="font-bold text-blue-800 mb-2">🎯 Crack Time Comparison:</h4>
                    <div className="font-mono text-sm space-y-1 text-blue-700">
                        <p>8 chars = ~2 days to crack</p>
                        <p>10 chars = ~5 months</p>
                        <p className="text-green-600 font-bold">12 chars = ~400 years</p>
                        <p className="text-green-600 font-bold">14 chars = ~16,000 years</p>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 4,
        title: "Password Managers",
        icon: "🗝️",
        content: (
            <div className="space-y-6">
                <div className="bg-green-50 p-6 rounded-2xl border-2 border-green-200">
                    <h3 className="font-bold text-xl text-green-800 mb-3">Your Best Friend</h3>
                    <p className="text-slate-700">
                        A <strong>password manager</strong> remembers all your passwords for you.
                        You only need to remember ONE strong master password!
                    </p>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-white p-5 rounded-xl border-2 border-green-200 shadow-sm">
                        <div className="text-3xl mb-3">🔐</div>
                        <h3 className="font-bold text-green-700 mb-2">One Password</h3>
                        <p className="text-slate-600 text-sm">
                            Remember just ONE strong master password. The manager remembers the rest.
                        </p>
                    </div>
                    <div className="bg-white p-5 rounded-xl border-2 border-green-200 shadow-sm">
                        <div className="text-3xl mb-3">🎲</div>
                        <h3 className="font-bold text-green-700 mb-2">Generates Strong Ones</h3>
                        <p className="text-slate-600 text-sm">
                            Creates random, uncrackable passwords like "x7K$mP2@nL9qRv" for every site.
                        </p>
                    </div>
                    <div className="bg-white p-5 rounded-xl border-2 border-green-200 shadow-sm">
                        <div className="text-3xl mb-3">🔄</div>
                        <h3 className="font-bold text-green-700 mb-2">Syncs Everywhere</h3>
                        <p className="text-slate-600 text-sm">
                            Access on phone, tablet, and computer. Auto-fills login forms.
                        </p>
                    </div>
                </div>
                <div className="bg-slate-100 p-4 rounded-xl">
                    <p className="text-slate-700 text-sm">
                        <strong>Popular Options:</strong> Bitwarden (free), 1Password, LastPass, Dashlane
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 5,
        title: "Two-Factor Authentication",
        icon: "📱",
        content: (
            <div className="space-y-6">
                <div className="bg-slate-800 p-6 rounded-2xl text-white">
                    <h3 className="font-bold text-xl mb-3">Your Second Line of Defense</h3>
                    <p className="text-slate-300">
                        Even if someone gets your password, <strong className="text-green-400">Two-Factor Authentication (2FA)</strong>
                        stops them. It requires something you HAVE (your phone) plus something you KNOW (password).
                    </p>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-green-50 p-4 rounded-xl border-2 border-green-200">
                        <h4 className="font-bold text-green-700 mb-2">✅ Best: Authenticator Apps</h4>
                        <p className="text-slate-600 text-sm">Google Authenticator, Authy, Microsoft Authenticator</p>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-xl border-2 border-yellow-200">
                        <h4 className="font-bold text-yellow-700 mb-2">⚠️ OK: SMS Codes</h4>
                        <p className="text-slate-600 text-sm">Better than nothing, but can be intercepted</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-xl border-2 border-blue-200">
                        <h4 className="font-bold text-blue-700 mb-2">🔑 Best: Hardware Keys</h4>
                        <p className="text-slate-600 text-sm">YubiKey, Google Titan - unhackable!</p>
                    </div>
                </div>
                <div className="bg-red-100 p-4 rounded-xl border border-red-200">
                    <h4 className="font-bold text-red-800 mb-2">🎮 Ready to Test Your Skills?</h4>
                    <p className="text-red-700 text-sm">
                        In the activity, you'll create a password and see how long it would take to crack!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ COMMON PASSWORDS ============
const COMMON_PASSWORDS = ['password', '123456', 'qwerty', 'admin', 'letmein', 'welcome', 'monkey', 'dragon', 'master', 'password1'];

// ============ PASSWORD TESTER COMPONENT ============
const PasswordTester = ({ onComplete }: { onComplete: () => void }) => {
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [crackTime, setCrackTime] = useState('Instant');
    const [strength, setStrength] = useState(0);
    const [completed, setCompleted] = useState(false);

    interface PasswordRule {
        id: string;
        label: string;
        check: (pw: string) => boolean;
        met: boolean;
    }

    const rules: PasswordRule[] = [
        { id: 'length', label: 'At least 12 characters', check: (pw) => pw.length >= 12, met: false },
        { id: 'upper', label: 'Contains uppercase letter', check: (pw) => /[A-Z]/.test(pw), met: false },
        { id: 'lower', label: 'Contains lowercase letter', check: (pw) => /[a-z]/.test(pw), met: false },
        { id: 'number', label: 'Contains a number', check: (pw) => /[0-9]/.test(pw), met: false },
        { id: 'special', label: 'Contains special character', check: (pw) => /[!@#$%^&*(),.?":{}|<>]/.test(pw), met: false },
        { id: 'nocommon', label: 'Not a common password', check: (pw) => !COMMON_PASSWORDS.includes(pw.toLowerCase()), met: false },
    ];

    const checkedRules = rules.map(r => ({ ...r, met: r.check(password) }));
    const metCount = checkedRules.filter(r => r.met).length;

    useEffect(() => {
        const newStrength = Math.round((metCount / rules.length) * 100);
        setStrength(newStrength);

        const charset =
            (/[a-z]/.test(password) ? 26 : 0) +
            (/[A-Z]/.test(password) ? 26 : 0) +
            (/[0-9]/.test(password) ? 10 : 0) +
            (/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 30 : 0);

        const combinations = Math.pow(charset || 1, password.length);
        const guessesPerSecond = 1e10;
        const seconds = combinations / guessesPerSecond;

        if (seconds < 1) setCrackTime('Instant');
        else if (seconds < 60) setCrackTime(`${Math.round(seconds)} seconds`);
        else if (seconds < 3600) setCrackTime(`${Math.round(seconds / 60)} minutes`);
        else if (seconds < 86400) setCrackTime(`${Math.round(seconds / 3600)} hours`);
        else if (seconds < 31536000) setCrackTime(`${Math.round(seconds / 86400)} days`);
        else if (seconds < 31536000 * 100) setCrackTime(`${Math.round(seconds / 31536000)} years`);
        else if (seconds < 31536000 * 1000000) setCrackTime(`${Math.round(seconds / 31536000 / 1000)} thousand years`);
        else setCrackTime('Centuries+');

        if (newStrength >= 80 && !completed && password.length > 0) {
            setCompleted(true);
            onComplete();
        }
    }, [password, metCount, completed, onComplete, rules.length]);

    const getStrengthColor = () => {
        if (strength < 30) return 'bg-red-500';
        if (strength < 60) return 'bg-orange-500';
        if (strength < 80) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    const getStrengthLabel = () => {
        if (strength < 30) return 'Weak';
        if (strength < 60) return 'Fair';
        if (strength < 80) return 'Good';
        return 'Strong';
    };

    return (
        <div className="space-y-6">
            <div className="text-center">
                <h2 className="text-3xl font-black text-slate-800 mb-2 flex items-center justify-center gap-3">
                    <Lock className="text-red-600" /> The Vault Tester
                </h2>
                <p className="text-slate-600">
                    See how long it would take a hacker to crack your password!
                </p>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-xl border-2 border-slate-100">
                {/* Password Input */}
                <div className="relative mb-6">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Type a password to test..."
                        className="w-full px-6 py-4 text-xl rounded-xl border-2 border-slate-200 focus:border-red-500 outline-none font-mono pr-12"
                    />
                    <button
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                        {showPassword ? <EyeOff size={24} /> : <Eye size={24} />}
                    </button>
                </div>

                {/* Strength Meter */}
                <div className="mb-6">
                    <div className="flex justify-between mb-2">
                        <span className="font-bold text-slate-600">Strength</span>
                        <span className={`font-bold ${strength < 30 ? 'text-red-600' : strength < 60 ? 'text-orange-600' : strength < 80 ? 'text-yellow-600' : 'text-green-600'}`}>
                            {getStrengthLabel()}
                        </span>
                    </div>
                    <div className="h-4 bg-slate-200 rounded-full overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${strength}%` }}
                            className={`h-full ${getStrengthColor()} transition-all`}
                        />
                    </div>
                </div>

                {/* Crack Time */}
                <div className="text-center p-6 bg-slate-900 rounded-2xl mb-6">
                    <div className="text-slate-400 text-sm mb-1">Time to crack (brute force at 10B guesses/sec)</div>
                    <div className={`text-3xl font-black ${crackTime === 'Instant' ? 'text-red-500' :
                        crackTime.includes('second') || crackTime.includes('minute') ? 'text-orange-500' :
                            crackTime.includes('hour') || crackTime.includes('day') ? 'text-yellow-500' :
                                'text-green-500'
                        }`}>
                        {crackTime}
                    </div>
                </div>

                {/* Rules Checklist */}
                <div className="grid grid-cols-2 gap-3">
                    {checkedRules.map(rule => (
                        <div
                            key={rule.id}
                            className={`flex items-center gap-3 p-3 rounded-xl border-2 ${rule.met ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                        >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${rule.met ? 'bg-green-500 text-white' : 'bg-slate-300'}`}>
                                {rule.met ? <Check size={14} /> : <X size={14} className="text-white" />}
                            </div>
                            <span className={rule.met ? 'text-green-700 font-bold' : 'text-slate-500'}>
                                {rule.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Completion Message */}
            {completed && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-100 p-6 rounded-2xl border-2 border-green-300 text-center"
                >
                    <CheckCircle className="text-green-600 mx-auto mb-3" size={48} />
                    <h3 className="text-xl font-bold text-green-800 mb-2">🎉 Module Complete!</h3>
                    <p className="text-green-700">You created a strong password that would take {crackTime} to crack!</p>
                </motion.div>
            )}

            {/* Tips */}
            <div className="bg-amber-50 rounded-2xl p-6 border-2 border-amber-200">
                <div className="flex items-start gap-3">
                    <AlertTriangle className="text-amber-600 flex-shrink-0 mt-1" size={24} />
                    <div>
                        <h3 className="font-bold text-amber-800 mb-2">Pro Tips for Strong Passwords</h3>
                        <ul className="text-amber-700 text-sm space-y-1">
                            <li>• Use a passphrase: "correct-horse-battery-staple" is stronger than "P@ssw0rd!"</li>
                            <li>• Never reuse passwords across sites</li>
                            <li>• Use a password manager to generate and store unique passwords</li>
                            <li>• Enable 2FA (Two-Factor Authentication) whenever possible</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const PasswordStrength = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('password-strength', 100);
    };

    return (
        <LessonWrapper
            moduleName="Password Strength"
            moduleEmoji="🔐"
            moduleColor="red"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Activity"
        >
            <PasswordTester onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default PasswordStrength;
